import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IOpcionReemplazo } from 'modelos/src';

/** Una fila del dialogo: un cliente que tiene que elegir a donde van sus vecinos. */
export interface IFilaReemplazo {
  /** Identifica la fila al devolver el resultado: id de cliente, o id de categoria. */
  clave: string;
  /** Encabezado de la fila: nombre del cliente, o de la categoria que se saca. */
  titulo: string;
  /** Vecinos con la categoria vigente. Si es 0 la fila es informativa. */
  vecinosVigentes: number;
  opciones: IOpcionReemplazo[];
  /** Preseleccion (por ejemplo, el clon cuando se clona una categoria compartida). */
  sugerido?: string | null;
  /** Detalle extra: "es la categoria por defecto", "2 rangos por edad", etc. */
  nota?: string;
}

export interface IDatosReemplazoDialog {
  titulo: string;
  /** Que se esta por hacer, en una linea. */
  descripcion: string;
  filas: IFilaReemplazo[];
  textoConfirmar: string;
}

/** `null` = los vecinos quedan sin categoria y pasan a la default del cliente. */
export type ResultadoReemplazo = Map<string, string | null>;

/**
 * Pide una categoria de reemplazo por cada cliente (o por cada categoria) afectado.
 *
 * Existe porque las categorias son globales: no tienen `idCliente`, asi que la misma categoria
 * puede estar en uso por varios clientes y cada uno necesita su propio reemplazo, elegido entre
 * las categorias que ESE cliente tiene asignadas.
 *
 * Las filas sin vecinos vigentes se muestran igual, para que se vea el alcance total del cambio,
 * pero no bloquean la confirmacion: sus referencias se limpian solas.
 */
@Component({
  selector: 'app-reemplazo-categoria-dialog',
  templateUrl: './reemplazo-categoria-dialog.component.html',
  styleUrls: ['./reemplazo-categoria-dialog.component.scss'],
  standalone: false,
})
export class ReemplazoCategoriaDialogComponent {
  /** Valor del select de cada fila. `undefined` = todavia sin elegir. */
  public elegido = new Map<string, string | null | undefined>();

  constructor(
    public dialogRef: MatDialogRef<ReemplazoCategoriaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IDatosReemplazoDialog,
  ) {
    for (const fila of data.filas) {
      this.elegido.set(
        fila.clave,
        fila.sugerido !== undefined ? fila.sugerido : undefined,
      );
    }
  }

  public seleccionar(clave: string, valor: string | null): void {
    this.elegido.set(clave, valor);
  }

  /** Una fila sin vecinos vigentes no necesita eleccion. */
  public requiereEleccion(fila: IFilaReemplazo): boolean {
    return fila.vecinosVigentes > 0;
  }

  /**
   * Un cliente sin ninguna otra categoria no tiene a donde mandar a sus vecinos: dejarlos sin
   * categoria los mandaria a la default, que tampoco va a existir. Hay que asignarle otra antes.
   */
  public sinSalida(fila: IFilaReemplazo): boolean {
    return this.requiereEleccion(fila) && !fila.opciones.length;
  }

  public get puedeConfirmar(): boolean {
    return this.data.filas.every(
      (fila) =>
        !this.requiereEleccion(fila) ||
        (!this.sinSalida(fila) && this.elegido.get(fila.clave) !== undefined),
    );
  }

  public confirmar(): void {
    const resultado: ResultadoReemplazo = new Map();
    for (const fila of this.data.filas) {
      const valor = this.elegido.get(fila.clave);
      resultado.set(fila.clave, valor === undefined ? null : valor);
    }
    this.dialogRef.close(resultado);
  }

  public cancelar(): void {
    this.dialogRef.close();
  }
}
